import React, { useState } from 'react';
import { useGACAStatus } from '../../hooks/useGACAStatus';
import { Activity, Wifi, WifiOff, Zap } from 'lucide-react';

interface StatusWidgetProps {
  variant?: 'badge' | 'detailed' | 'minimal';
  className?: string;
}

export const StatusWidget: React.FC<StatusWidgetProps> = ({
  variant = 'badge',
  className = '',
}) => {
  const { status, health, isLoading, lastUpdated } = useGACAStatus();
  const [showTooltip, setShowTooltip] = useState(false);

  const getStatusColor = () => {
    if (!status || status.status === 'down') return 'bg-red-500';
    if (status.status === 'degraded') return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStatusText = () => {
    if (!status || status.status === 'down') return 'Niedostępny';
    if (status.status === 'degraded') return 'Ograniczony';
    return 'Online';
  };

  const getStatusIcon = () => {
    if (!status || status.status === 'down') return <WifiOff className="w-3 h-3" />;
    if (status.status === 'degraded') return <Activity className="w-3 h-3" />;
    return <Wifi className="w-3 h-3" />;
  };

  if (variant === 'minimal') {
    return (
      <div
        className={`inline-flex items-center gap-1 ${className}`}
        title={`G.A.C.A. ${getStatusText()}`}
      >
        <span className={`w-2 h-2 rounded-full ${getStatusColor()} animate-pulse`} />
      </div>
    );
  }

  if (variant === 'badge') {
    return (
      <div
        className={`relative inline-flex ${className}`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div
          className={`
            inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium
            bg-white/10 dark:bg-white/5 backdrop-blur-sm
            border border-white/20 dark:border-white/10
            text-text-primary-light dark:text-text-primary-dark
            transition-all duration-200 hover:bg-white/20
            cursor-default
          `}
        >
          <span className={`w-2 h-2 rounded-full ${getStatusColor()} ${isLoading ? 'animate-pulse' : ''}`} />
          <span className="hidden sm:inline">{status?.activeProviders ?? 0}</span>
          <Zap className="w-3 h-3 text-primary-light dark:text-primary-dark" />
        </div>

        {/* Tooltip */}
        {showTooltip && (
          <div
            className={`
              absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50
              w-64 p-3 rounded-xl
              bg-content-light dark:bg-content-dark
              border border-border-light dark:border-border-dark
              shadow-xl
              text-sm
            `}
          >
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border-light dark:border-border-dark">
              <Zap className="w-4 h-4 text-primary-light dark:text-primary-dark" />
              <span className="font-semibold">G.A.C.A. Status</span>
            </div>

            <div className="space-y-1.5 text-text-secondary-light dark:text-text-secondary-dark">
              <div className="flex justify-between">
                <span>Status:</span>
                <span className={`font-medium ${
                  status?.status === 'healthy' ? 'text-green-500' :
                  status?.status === 'degraded' ? 'text-yellow-500' : 'text-red-500'
                }`}>
                  {getStatusText()}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Aktywni providerzy:</span>
                <span className="font-medium text-text-primary-light dark:text-text-primary-dark">
                  {status?.activeProviders ?? 0} / {status?.totalProviders ?? 7}
                </span>
              </div>
              {health?.version && (
                <div className="flex justify-between">
                  <span>Wersja:</span>
                  <span className="font-mono text-xs">{health.version}</span>
                </div>
              )}
              {lastUpdated && (
                <div className="flex justify-between text-xs mt-2 pt-2 border-t border-border-light dark:border-border-dark">
                  <span>Ostatnia aktualizacja:</span>
                  <span>{lastUpdated.toLocaleTimeString('pl-PL')}</span>
                </div>
              )}
            </div>

            {/* Provider list */}
            {status?.providers && status.providers.length > 0 && (
              <div className="mt-2 pt-2 border-t border-border-light dark:border-border-dark">
                <div className="text-xs font-medium mb-1">Providerzy:</div>
                <div className="flex flex-wrap gap-1">
                  {status.providers.slice(0, 5).map((provider) => (
                    <span
                      key={provider.name}
                      className={`
                        px-1.5 py-0.5 rounded text-xs
                        ${provider.status === 'online'
                          ? 'bg-green-500/20 text-green-600 dark:text-green-400'
                          : provider.status === 'rate_limited'
                          ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400'
                          : 'bg-red-500/20 text-red-600 dark:text-red-400'
                        }
                      `}
                    >
                      {provider.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Arrow */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
              <div className="w-3 h-3 rotate-45 bg-content-light dark:bg-content-dark border-r border-b border-border-light dark:border-border-dark" />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Detailed variant
  return (
    <div
      className={`
        p-4 rounded-xl
        bg-white/10 dark:bg-white/5 backdrop-blur-md
        border border-white/20 dark:border-white/10
        ${className}
      `}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-primary-light dark:text-primary-dark" />
          <span className="font-semibold text-text-primary-light dark:text-text-primary-dark">
            G.A.C.A. Multi-Provider AI
          </span>
        </div>
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
          status?.status === 'healthy' ? 'bg-green-500/20 text-green-600' :
          status?.status === 'degraded' ? 'bg-yellow-500/20 text-yellow-600' :
          'bg-red-500/20 text-red-600'
        }`}>
          {getStatusIcon()}
          <span>{getStatusText()}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="p-2 rounded-lg bg-white/5">
          <div className="text-text-secondary-light dark:text-text-secondary-dark text-xs">
            Aktywni providerzy
          </div>
          <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
            {status?.activeProviders ?? 0}
            <span className="text-sm font-normal text-text-secondary-light dark:text-text-secondary-dark">
              /{status?.totalProviders ?? 7}
            </span>
          </div>
        </div>
        <div className="p-2 rounded-lg bg-white/5">
          <div className="text-text-secondary-light dark:text-text-secondary-dark text-xs">
            Dostępne modele
          </div>
          <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
            34+
          </div>
        </div>
      </div>

      {status?.providers && status.providers.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="flex flex-wrap gap-1.5">
            {status.providers.map((provider) => (
              <span
                key={provider.name}
                className={`
                  px-2 py-1 rounded-lg text-xs font-medium
                  ${provider.status === 'online'
                    ? 'bg-green-500/20 text-green-600 dark:text-green-400'
                    : provider.status === 'rate_limited'
                    ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400'
                    : 'bg-red-500/20 text-red-600 dark:text-red-400'
                  }
                `}
              >
                {provider.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusWidget;
